import React, { useState, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { useNotes } from '../../contexts/NotesContext'
import { useFolders } from '../../contexts/FoldersContext'
import { useTabs } from '../../contexts/TabsContext'

/**
 * NoteLinkModal Component
 * 
 * Modal for selecting notes to link in the rich text editor.
 * Based on DrawingModal's excellent mobile-first design patterns.
 * Features glassmorphism design, responsive layouts, and theme awareness.
 */
const NoteLinkModal = ({ 
  isOpen, 
  onClose, 
  onSelectNote, 
  searchTerm = '',
  currentNoteId = null // Exclude current note from selection
}) => {
  const { notes } = useNotes()
  const { folders } = useFolders()
  const { openTab } = useTabs()
  
  const [filteredNotes, setFilteredNotes] = useState([])
  const [searchInput, setSearchInput] = useState(searchTerm)
  const [isProcessing, setIsProcessing] = useState(false)
  
  /**
   * Check if a note is sensitive (same logic as NotesContext)
   */
  const isNoteSensitive = useCallback((note) => {
    if (!note.folderId) return false
    const noteFolder = folders.find(f => f.folderId === note.folderId)
    return noteFolder?.folderType === 'SENSITIVE'
  }, [folders])
  
  /**
   * Get non-sensitive notes (only show "All Notes")
   */
  const getNonSensitiveNotes = useCallback(() => {
    return notes.filter(note => {
      // Exclude sensitive notes
      if (isNoteSensitive(note)) return false
      
      // Exclude current note to prevent self-linking
      if (currentNoteId && (note.noteId === currentNoteId || note.id === currentNoteId)) {
        return false
      }
      
      return true
    })
  }, [notes, isNoteSensitive, currentNoteId])
  
  /**
   * Filter and search notes
   */
  useEffect(() => {
    const nonSensitiveNotes = getNonSensitiveNotes()
    
    if (!searchInput.trim()) {
      // Show recent 10 non-sensitive notes
      const recentNotes = nonSensitiveNotes
        .sort((a, b) => {
          const aTime = new Date(a.lastModified || a.updatedAt || a.createdAt || 0)
          const bTime = new Date(b.lastModified || b.updatedAt || b.createdAt || 0)
          return bTime - aTime
        })
        .slice(0, 10)
      setFilteredNotes(recentNotes)
    } else {
      // Search within non-sensitive notes only
      const searchTerm = searchInput.toLowerCase()
      const filtered = nonSensitiveNotes.filter(note => 
        note.title?.toLowerCase().includes(searchTerm) ||
        note.content?.toLowerCase().includes(searchTerm)
      ).slice(0, 10)
      setFilteredNotes(filtered)
    }
  }, [searchInput, getNonSensitiveNotes])
  
  /**
   * Handle note selection
   */
  const handleSelectNote = useCallback(async (note) => {
    // Double-check that we're not selecting a sensitive note
    if (isNoteSensitive(note)) {
      console.warn('⚠️ NoteLinkModal: Attempted to select sensitive note, blocking')
      return
    }
    
    setIsProcessing(true)
    
    try {
      await onSelectNote({
        noteId: note.noteId || note.id,
        title: note.title || 'Untitled'
      })
      onClose()
    } catch (error) {
      console.error('Failed to select note:', error)
    } finally {
      setIsProcessing(false)
    }
  }, [isNoteSensitive, onSelectNote, onClose])
  
  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    if (isProcessing) return
    onClose()
  }, [isProcessing, onClose])
  
  /**
   * Handle search input change with debouncing
   */
  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value)
  }, [])
  
  /**
   * Get folder name for a note
   */
  const getFolderName = useCallback((note) => {
    if (!note.folderId) return null
    const folder = folders.find(f => f.folderId === note.folderId)
    return folder?.name || 'Unknown Folder'
  }, [folders])
  
  /**
   * Format note content preview
   */
  const getContentPreview = useCallback((content, maxLength = 80) => {
    if (!content) return ''
    // Strip HTML tags and get preview
    const plainText = content.replace(/<[^>]*>/g, '')
    return plainText.length > maxLength 
      ? plainText.substring(0, maxLength) + '...'
      : plainText
  }, [])
  
  // Debug logging for mobile issues
  useEffect(() => {
    if (isOpen) {
      console.log('🔗 NoteLinkModal: Modal opened')
      console.log('🔗 NoteLinkModal: Mobile device:', window.innerWidth < 768)
      console.log('🔗 NoteLinkModal: Search term:', searchTerm)
      console.log('🔗 NoteLinkModal: Current note ID:', currentNoteId)
      console.log('🔗 NoteLinkModal: Available notes count:', notes.length)
      console.log('🔗 NoteLinkModal: Available folders count:', folders.length)
    }
  }, [isOpen, searchTerm, currentNoteId, notes.length, folders.length])

  if (!isOpen) {
    return null
  }

  console.log('🔗 NoteLinkModal: Rendering modal')

  return ReactDOM.createPortal(
    <>
      {/* Custom scrollbar styles (same as DrawingModal) */}
      <style>{`
        .notes-list-container::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .notes-list-container::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 4px;
        }
        .notes-list-container::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.6);
          border-radius: 4px;
        }
        .notes-list-container::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.8);
        }
        .notes-list-container {
          -webkit-overflow-scrolling: touch;
        }
      `}</style>
      
      {/* Modal Backdrop */}
      <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[9999] p-2 sm:p-4">
        
        {/* Modal Container */}
        <div className="bg-white/40 dark:bg-gray-800/40 backdrop-blur-[40px] rounded-2xl shadow-2xl border border-white/30 dark:border-gray-700/30 max-w-lg w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col mx-2 sm:mx-4" 
             style={{ backdropFilter: 'blur(40px)' }}>
          
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Link to Note
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Choose from All Notes (sensitive notes excluded)
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
              title="Close"
              disabled={isProcessing}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search Bar */}
          <div className="px-6 py-4 border-b border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search notes by title or content..."
                value={searchInput}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-3 text-base bg-white/50 dark:bg-gray-700/50 border border-gray-200/50 dark:border-gray-600/50 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm transition-all duration-200"
                autoFocus
                disabled={isProcessing}
              />
            </div>
          </div>

          {/* Notes List */}
          <div className="notes-list-container flex-1 min-h-0 bg-gradient-to-br from-gray-50/40 to-gray-100/40 dark:from-gray-900/40 dark:to-gray-800/40 backdrop-blur-[40px] overflow-auto" 
               style={{ 
                 backdropFilter: 'blur(40px)',
                 scrollbarWidth: 'thin',
                 scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent'
               }}>
            
            {filteredNotes.length > 0 ? (
              <div className="divide-y divide-gray-200/30 dark:divide-gray-700/30">
                {filteredNotes.map(note => {
                  const folderName = getFolderName(note)
                  const contentPreview = getContentPreview(note.content)
                  
                  return (
                    <div
                      key={note.noteId || note.id}
                      onClick={() => handleSelectNote(note)}
                      className="p-4 sm:p-6 hover:bg-white/30 dark:hover:bg-gray-700/30 active:bg-white/40 dark:active:bg-gray-600/40 cursor-pointer transition-all duration-200 group"
                      disabled={isProcessing}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {note.title || 'Untitled Note'}
                          </h4>
                          
                          {contentPreview && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                              {contentPreview}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-500">
                            {folderName && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                                </svg>
                                {folderName}
                              </span>
                            )}
                            
                            {note.lastModified && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {new Date(note.lastModified).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex-shrink-0">
                          <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <div className="w-16 h-16 bg-gray-200/50 dark:bg-gray-700/50 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">
                  {searchInput.trim() ? 'No matching notes found' : 'No notes available to link'}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  {searchInput.trim() 
                    ? 'Try a different search term or browse all notes'
                    : 'Only notes from "All Notes" can be linked (sensitive notes excluded)'
                  }
                </p>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-4 sm:px-6 py-4 border-t border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
            
            {/* Mobile: Single row layout */}
            <div className="flex items-center justify-between gap-3 sm:hidden">
              <div className="text-xs text-gray-500 dark:text-gray-500">
                {filteredNotes.length} note{filteredNotes.length !== 1 ? 's' : ''}
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-200/50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-300/50 dark:hover:bg-gray-600/50 hover:shadow-md transition-all duration-200 backdrop-blur-sm"
                disabled={isProcessing}
              >
                Cancel
              </button>
            </div>

            {/* Desktop: Split layout */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-500">
                {filteredNotes.length} note{filteredNotes.length !== 1 ? 's' : ''} available to link
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-200/50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-300/50 dark:hover:bg-gray-600/50 hover:shadow-md transition-all duration-200 backdrop-blur-sm"
                disabled={isProcessing}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}

export default NoteLinkModal
